import { useCallback, useEffect, useState, useRef, useMemo } from "react";

import api from "api/api";
import { AgGridReact } from "ag-grid-react";

// mui components
import Button from "@mui/material/Button";

// mui icons
import ControlPointOutlinedIcon from "@mui/icons-material/ControlPointOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import EditIcon from "@mui/icons-material/Edit";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";

import Loader from "../common/Loader";

import { useSelector, useDispatch } from "react-redux";
import { showNotification } from "store/features/notificationSlice";
import SettingsAddUpdate from "./SettingsAddUpdate";
import RespondentSettingsData from "../../utils/RespondentSettingsData";
import respondentSettingsUtils from "../../utils/respondentSettingsUtils"; // Import the utility

function Settings() {
    const gridRef = useRef();
    const dispatch = useDispatch();

    const productCategoryData = useSelector((state) => state.settingsStore.productCategory);
    const productCategoryId = productCategoryData?.value || null;
    const productCategoryLabel = productCategoryData?.label || null; // Get product category label

    // const [allRowData, setAllRowData] = useState([]);
    const [rowData, setRowData] = useState([]);

    const [selectedRows, setSelectedRows] = useState([]);
    const [selectedRowToUpdate, setSelectedRowToUpdate] = useState([]);

    const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
    const [isUpdateDialogOpen, setIsUpdateDialogOpen] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const [uniqueBackendVariableList, setUniqueBackendVariableList] = useState([]);

    // Get filtered types_of_variable based on product category using the utility function
    const getTypesOfVariableForCell = useCallback(() => {
        return respondentSettingsUtils(productCategoryLabel);
    }, [productCategoryLabel]);

    const columnDefs = [
        {
            headerName: "Backend Variable",
            field: "backend_variable",
            minWidth: 320,
            checkboxSelection: true,
            headerCheckboxSelection: true,
            autoHeight: true,
            filter: true,
            suppressMenu: true,
            floatingFilter: true,
            filterParams: {
                filterOptions: ["contains"], // to get filter remove class .ag-floating-filter-button  form css
                // filterOptions: ["equals", "contains", "startsWith", "endsWith"], // to get filter remove class .ag-floating-filter-button  form css
            },
        },
        {
            headerName: "Display Label",
            field: "display_label",
            minWidth: 320,

            autoHeight: true,
            filter: true,
            suppressMenu: true,
            floatingFilter: true,
            filterParams: {
                filterOptions: ["contains"], // to get filter remove class .ag-floating-filter-button  form css
                // filterOptions: ["equals", "contains", "startsWith", "endsWith"], // to get filter remove class .ag-floating-filter-button  form css
            },
        },
        {
            headerName: "Variable Category",
            field: "variable_category",
            minWidth: 320,

            autoHeight: true,
            filter: true,
            suppressMenu: true,
            floatingFilter: true,
            filterParams: {
                filterOptions: ["contains"], // to get filter remove class .ag-floating-filter-button  form css
                // filterOptions: ["equals", "contains", "startsWith", "endsWith"], // to get filter remove class .ag-floating-filter-button  form css
            },
        },
        {
            headerName: "Type of Variable",
            field: "type_of_variable",
            minWidth: 200,

            autoHeight: true,
            filter: true,
            suppressMenu: true,
            floatingFilter: true,
            filterParams: {
                filterOptions: ["contains"], // to get filter remove class .ag-floating-filter-button  form css
                // filterOptions: ["equals", "contains", "startsWith", "endsWith"], // to get filter remove class .ag-floating-filter-button  form css
            },
            cellRenderer: (row) => {
                const options = getTypesOfVariableForCell();
                return options.filter((ele) => ele.value === row.value)?.[0]?.label || row.value;
            },
        },
        {
            headerName: "Type of Fixed Graph",
            field: "type_of_graph",
            minWidth: 200,

            autoHeight: true,
            filter: true,
            suppressMenu: true,
            floatingFilter: true,
            filterParams: {
                filterOptions: ["contains"], // to get filter remove class .ag-floating-filter-button  form css
                // filterOptions: ["equals", "contains", "startsWith", "endsWith"], // to get filter remove class .ag-floating-filter-button  form css
            },
            cellRenderer: (row) => {
                return RespondentSettingsData.types_of_graph.filter((ele) => ele.value === row.value)?.[0]?.label || row.value.charAt(0).toUpperCase() + row.value.slice(1).toLowerCase() || row.value;
            },
        },
        {
            headerName: "Active/Retired",
            field: "active_retired_label",
            minWidth: 160,

            autoHeight: true,
            filter: true,
            suppressMenu: true,
            floatingFilter: true,
            filterParams: {
                filterOptions: ["contains"], // to get filter remove class .ag-floating-filter-button  form css
                // filterOptions: ["equals", "contains", "startsWith", "endsWith"], // to get filter remove class .ag-floating-filter-button  form css
            },
            // cellRenderer: (row) => {
            //     return row.value ? "Active" : "Retired";
            // },
        },
        {
            headerName: "Last edited by",
            field: "updated_by__username",
            minWidth: 280,

            autoHeight: true,
            filter: true,
            suppressMenu: true,
            floatingFilter: true,
            filterParams: {
                filterOptions: ["contains"], // to get filter remove class .ag-floating-filter-button  form css
                // filterOptions: ["equals", "contains", "startsWith", "endsWith"], // to get filter remove class .ag-floating-filter-button  form css
            },
        },
        {
            headerName: "Action",
            maxWidth: 120,
            cellRenderer: (row) => (
                <EditIcon
                    color="primary"
                    style={{
                        cursor: "pointer",
                    }}
                    onClick={() => handleUpdateClick(row)}
                />
            ),
        },
    ];

    const handleUpdateClick = (row) => {
        setSelectedRowToUpdate(row.data);
        setIsUpdateDialogOpen(true);
    };

    const defaultColDef = useMemo(() => {
        return {
            editable: false,
            sortable: true,
            resizable: true,
            cellStyle: {
                fontSize: "14px",
                border: "none",
            },
            minWidth: 200,
        };
    }, []);

    const sortingOrder = useMemo(() => {
        return ["desc", "asc", null];
    }, []);

    const onSelectionChanged = useCallback(() => {
        let _selectedRows = gridRef.current.api.getSelectedRows();
        setSelectedRows(_selectedRows);
    }, []);

    const fetchData = useCallback(() => {
        setIsLoading(true);
        let url = process.env.REACT_APP_SERVER_PATH + `/respondent-settings/all/?archive=False`;
        if (productCategoryId) {
            url += `&product_category=${productCategoryId}`;
        }
        try {
            api.get(url)
                .then(async (res) => {
                    const _data =
                        res?.data?.map((row) => {
                            return {
                                ...row,
                                active_retired_label: row?.active ? "Active" : "Retired",
                            };
                        }) || [];
                    setRowData([..._data]);
                    setIsLoading(false);
                })
                .catch(async (error) => {
                    handleError("error", error);
                    setIsLoading(false);
                });
        } catch (error) {
            handleError("error", error);
            setIsLoading(false);
        }
    }, [productCategoryId]);

    const fetchUniqueBackendVariableData = useCallback(() => {
        setIsLoading(true);
        let url = process.env.REACT_APP_SERVER_PATH + `/respondent-settings/data-mappings/`;
        if (productCategoryId) {
            url += `?product_category=${productCategoryId}`;
        }
        try {
            api.get(url)
                .then(async (res) => {
                    setUniqueBackendVariableList([...res.data]);
                })
                .catch(async (error) => {
                    handleError("error", error);
                });
        } catch (error) {
            handleError("error", error);
        }
    }, [productCategoryId]);

    useEffect(() => {
        fetchData();
        fetchUniqueBackendVariableData();
    }, [fetchData, fetchUniqueBackendVariableData]);

    const handleError = (type, error) => {
        dispatch(showNotification({ severity: type, data: error }));
    };

    const handleDelete = () => {
        const selectedIds = selectedRows.map((datum) => datum.id);
        const payload = { ids: selectedIds };
        try {
            let url = process.env.REACT_APP_SERVER_PATH + `/respondent-settings/multiple-delete/`;
            api.put(url, payload)
                .then(() => {
                    fetchData();
                    handleConfirmationDialogClose();
                })
                .catch((error) => {
                    handleError("error", error);
                });
        } catch (error) {
            handleError("error", error);
        }
    };

    const handleConfirmationDialogClose = () => {
        setIsDeleteDialogOpen(false);
        setSelectedRows([]);
    };

    return (
        <>
            {isLoading && <Loader />}
            <div
                className="ag-theme-alpine"
                style={{
                    height: "calc(100vh - 225px)",
                    width: "100%",
                    position: "relative",
                }}
            >
                <div
                    style={{
                        display: "flex",
                        justifyContent: "end",
                        marginBottom: 10,
                    }}
                >
                    <Button
                        size="small"
                        className="custom-btn custom-btn-outlined color-blue me-2"
                        variant="outlined"
                        startIcon={<ControlPointOutlinedIcon />}
                        onClick={() => setIsAddDialogOpen(true)}
                    >
                        Add
                    </Button>
                    <Button
                        size="small"
                        className="custom-btn"
                        variant="outlined"
                        startIcon={<DeleteOutlinedIcon />}
                        style={
                            selectedRows.length === 0
                                ? {
                                      color: "rgba(0, 0, 0, 0.26)",
                                      borderColor: "rgba(0, 0, 0, 0.12)",
                                  }
                                : {
                                      color: "red",
                                      borderColor: "red",
                                  }
                        }
                        onClick={() => setIsDeleteDialogOpen(true)}
                        disabled={selectedRows.length === 0}
                    >
                        Delete
                    </Button>
                </div>
                <style>
                    {`
                        .ag-header-group-cell-label {
                            justify-content: center !important;
                        }
                        .ag-header-cell-comp-wrapper {
                            justify-content: center !important;
                        }
                    `}
                </style>
                <AgGridReact
                    rowStyle={{
                        background: "white",
                    }}
                    ref={gridRef}
                    rowSelection="multiple"
                    rowMultiSelectWithClick={true}
                    defaultColDef={defaultColDef}
                    rowData={rowData}
                    columnDefs={columnDefs}
                    onGridReady={(params) => params.id}
                    onSelectionChanged={onSelectionChanged}
                    // sideBar={false}
                    rowHeight={35}
                    headerHeight={40}
                    floatingFiltersHeight={40}
                    suppressDragLeaveHidesColumns={true}
                    sortingOrder={sortingOrder}
                    suppressRowClickSelection={true}
                ></AgGridReact>
            </div>
            <div className="d-flex">
                <span
                    style={{
                        fontSize: 12,
                        color: "#495057",
                        marginRight: 20,
                        fontWeight: 600,
                    }}
                >
                    Selected rows: {selectedRows.length}
                </span>
                <span
                    style={{
                        fontSize: 12,
                        color: "#495057",
                        fontWeight: 600,
                    }}
                >
                    Total rows: {rowData.length}
                </span>
            </div>
            {isAddDialogOpen && (
                <SettingsAddUpdate
                    title="Add New Variable for Respondent Dashboard"
                    type="add"
                    isOpen={isAddDialogOpen}
                    setIsAddDialogOpen={setIsAddDialogOpen}
                    fetchData={fetchData}
                    uniqueBackendVariableList={uniqueBackendVariableList}
                    productCategoryLabel={productCategoryLabel} // Pass product category label
                />
            )}
            {isUpdateDialogOpen && (
                <SettingsAddUpdate
                    title="Update Variable for Respondent Dashboard"
                    type="update"
                    selectedRows={selectedRowToUpdate}
                    isOpen={isUpdateDialogOpen}
                    setIsUpdateDialogOpen={setIsUpdateDialogOpen}
                    fetchData={fetchData}
                    uniqueBackendVariableList={uniqueBackendVariableList}
                    productCategoryLabel={productCategoryLabel} // Pass product category label
                />
            )}
            <div></div>
            {isDeleteDialogOpen && (
                <Dialog
                    sx={{
                        "& .MuiDialog-paper": {
                            maxWidth: "660px",
                            width: "100%",
                        },
                    }}
                    open={isDeleteDialogOpen}
                    aria-describedby="alert-dialog-slide-description"
                >
                    <div
                        style={{
                            padding: "10px 20px",
                            color: "rgb(92, 92, 92)",
                        }}
                    >
                        <div>
                            <p
                                style={{
                                    fontWeight: 600,
                                }}
                            >
                                Are you sure you want to delete the following settings?
                            </p>
                        </div>
                        <div>
                            <table
                                style={{
                                    fontSize: "14px",
                                    width: "100%",
                                    borderCollapse: "collapse",
                                    fontFamily: "Arial, sans-serif",
                                }}
                            >
                                <thead>
                                    <tr
                                        style={{
                                            backgroundColor: "#F2F2F2",
                                        }}
                                    >
                                        <th
                                            style={{
                                                border: "1px solid #dddddd",
                                                textAlign: "left",
                                                padding: "8px",
                                            }}
                                        >
                                            Sl No.
                                        </th>
                                        <th
                                            style={{
                                                border: "1px solid #dddddd",
                                                textAlign: "left",
                                                padding: "8px",
                                            }}
                                        >
                                            Backend Variable
                                        </th>
                                        <th
                                            style={{
                                                border: "1px solid #dddddd",
                                                textAlign: "left",
                                                padding: "8px",
                                            }}
                                        >
                                            Display Label
                                        </th>
                                        <th
                                            style={{
                                                border: "1px solid #dddddd",
                                                textAlign: "left",
                                                padding: "8px",
                                            }}
                                        >
                                            Variable Category
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {selectedRows.map((ele, i) => (
                                        <tr key={ele.backend_variable}>
                                            <td
                                                style={{
                                                    border: "1px solid #dddddd",
                                                    padding: "8px",
                                                }}
                                            >
                                                {i + 1}
                                            </td>
                                            <td
                                                style={{
                                                    border: "1px solid #dddddd",
                                                    padding: "8px",
                                                }}
                                            >
                                                {ele.backend_variable}
                                            </td>
                                            <td
                                                style={{
                                                    border: "1px solid #dddddd",
                                                    padding: "8px",
                                                }}
                                            >
                                                {ele.display_label}
                                            </td>
                                            <td
                                                style={{
                                                    border: "1px solid #dddddd",
                                                    padding: "8px",
                                                }}
                                            >
                                                {ele.variable_category}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <DialogActions>
                        <Button
                            className="me-2 custom-btn liner-light-blue-background"
                            size="small"
                            variant="contained"
                            color="error"
                            onClick={handleConfirmationDialogClose}
                        >
                            Disagree
                        </Button>
                        <Button
                            className="me-2 custom-btn liner-light-blue-background"
                            size="small"
                            variant="contained"
                            onClick={handleDelete}
                        >
                            Agree
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
        </>
    );
}

export default Settings;
